/**
 * TokenGateContext
 *
 * Context for managing the InsufficientTokensModal and token gating operations.
 * Provides both callback-based and promise-based APIs for checking tokens.
 *
 * Usage:
 * - checkTokens(required, operationName, onSuccess) - Callback-based API
 * - requireTokens(required, operationName) - Promise-based API
 */

import { createContext, useState, useCallback, type ReactNode } from 'react';
import { InsufficientTokensModal } from '../components/tokens/InsufficientTokensModal';
import { useTokenBalance } from '../hooks/useTokenBalance';
import { costGateStatus } from '../utils/costGate';

// ============================================================================
// Types
// ============================================================================

interface TokenGateContextType {
  /**
   * Check if user has enough tokens. If not, shows modal. If yes, calls onSuccess.
   * @param required - Number of tokens required
   * @param operationName - Human-readable operation name for display
   * @param onSuccess - Callback to execute if user has enough tokens (or after purchase)
   */
  checkTokens: (required: number, operationName: string, onSuccess: () => void) => void;

  /**
   * Promise-based token check. Returns true if user has enough tokens.
   * Shows modal if insufficient. Resolves false if user closes modal without purchasing.
   * @param required - Number of tokens required
   * @param operationName - Human-readable operation name for display
   * @returns Promise<boolean> - true if tokens are available, false if not
   */
  requireTokens: (required: number, operationName: string) => Promise<boolean>;

  /**
   * Open the purchase modal because the SERVER refused the operation for lack
   * of tokens (HTTP 402 / INSUFFICIENT_TOKENS).
   *
   * Deliberately does NOT re-check the local balance the way `checkTokens` and
   * `requireTokens` do: the server has already answered, and the cached balance
   * here can easily be the stale one that let the request through in the first
   * place. Re-checking would swallow the refusal and leave the user with a
   * failure and no explanation.
   *
   * @param required - The quote the operation was priced at, when the surface
   *   knows it (it is what the cost card displayed). Pass `null` when the
   *   surface shows no quote — the modal then omits the deficit line rather
   *   than inventing a number.
   * @param operationName - Human-readable operation name for display.
   */
  reportInsufficientTokens: (required: number | null, operationName: string) => void;
}

interface ModalState {
  isOpen: boolean;
  requiredTokens: number | null;
  operationName: string;
  onRetry?: () => void;
}

// ============================================================================
// Context
// ============================================================================

// eslint-disable-next-line react-refresh/only-export-components
export const TokenGateContext = createContext<TokenGateContextType | undefined>(undefined);

// ============================================================================
// Provider
// ============================================================================

interface TokenGateProviderProps {
  children: ReactNode;
}

export function TokenGateProvider({ children }: TokenGateProviderProps) {
  const { balance } = useTokenBalance();
  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    requiredTokens: 0,
    operationName: '',
  });

  // Resolver for promise-based API
  const [resolver, setResolver] = useState<((value: boolean) => void) | null>(null);

  // Close modal
  const closeModal = useCallback(() => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
    // Resolve promise with false if user closes without purchasing
    if (resolver) {
      resolver(false);
      setResolver(null);
    }
  }, [resolver]);

  // Callback-based API
  const checkTokens = useCallback(
    (required: number, operationName: string, onSuccess: () => void) => {
      // An unknown balance never blocks: the server is the authority and
      // answers 402 when the account cannot pay (src/utils/costGate.ts).
      if (costGateStatus(required, balance) === 'affordable') {
        // User has enough tokens, execute immediately
        onSuccess();
      } else {
        // Show modal with retry callback
        setModalState({
          isOpen: true,
          requiredTokens: required,
          operationName,
          onRetry: onSuccess,
        });
      }
    },
    [balance]
  );

  // Promise-based API
  const requireTokens = useCallback(
    (required: number, operationName: string): Promise<boolean> => {
      return new Promise((resolve) => {
        if (costGateStatus(required, balance) === 'affordable') {
          // User has enough tokens
          resolve(true);
        } else {
          // Store resolver and show modal
          setResolver(() => resolve);
          setModalState({
            isOpen: true,
            requiredTokens: required,
            operationName,
          });
        }
      });
    },
    [balance]
  );

  // Server-refusal API — see reportInsufficientTokens' doc on the context type
  // for why this one does not consult `balance`.
  const reportInsufficientTokens = useCallback(
    (required: number | null, operationName: string): void => {
      setModalState({
        isOpen: true,
        requiredTokens: required,
        operationName,
      });
    },
    []
  );

  const value: TokenGateContextType = {
    checkTokens,
    requireTokens,
    reportInsufficientTokens,
  };

  return (
    <TokenGateContext.Provider value={value}>
      {children}
      <InsufficientTokensModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        requiredTokens={modalState.requiredTokens}
        operationName={modalState.operationName}
      />
    </TokenGateContext.Provider>
  );
}
